if (!customElements.get('product-card-image-gallery')) {
  const PRODUCT_CARD_IMAGE_FADE_MS = 220;

function normalizeProductCardImageSrc(src) {
  return src ? src.split('?')[0] : '';
}

function setProductCardImageSource(image, src, srcset) {
  if (!image || !src) return;
  image.src = src;
  if (srcset) {
    image.srcset = srcset;
  } else {
    image.removeAttribute('srcset');
  }
  if (image.dataset.src) {
    image.dataset.src = src;
  }
}

function parseProductCardGalleryImages(raw) {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((image) => image?.src) : [];
  } catch (error) {
    return [];
  }
}

function getSwatchHoverImage(swatch) {
  if (!swatch) return null;

  const gallery = parseProductCardGalleryImages(swatch.dataset.galleryImages);
  if (gallery.length > 1) return gallery[1];

  const primarySrc = swatch.dataset.primarySrc;
  if (primarySrc) {
    return { src: primarySrc, srcset: swatch.dataset.primarySrcset || '' };
  }

  return gallery[0] || null;
}

class ProductCardImageGallery extends HTMLElement {
  constructor() {
    super();
    this.onNavClick = this.onNavClick.bind(this);
    this.onColorSelected = this.onColorSelected.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onProductLinkClick = this.onProductLinkClick.bind(this);
    this.onImageAreaPointerEnter = this.onImageAreaPointerEnter.bind(this);
    this.onImageAreaPointerLeave = this.onImageAreaPointerLeave.bind(this);
    this.onImageAreaMouseOver = this.onImageAreaMouseOver.bind(this);
    this.onNavPointerDown = this.onNavPointerDown.bind(this);
    this.images = [];
    this.loadedSrc = new Set();
    this.currentIndex = 0;
    this.isImageAreaHovered = false;
    this.isTouchBrowsing = false;
    this._suppressLinkClick = false;
    this._hoverSyncFrame = null;
    this._hoverExitTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isSwitching = false;
    this.fadeDuration = PRODUCT_CARD_IMAGE_FADE_MS;
  }

  connectedCallback() {
    this.card = this.closest('[data-product-card]');
    if (!this.card) return;

    this.imageArea = this.card.querySelector('.product--card-image');
    this.primaryImage = this.card.querySelector('[data-card-primary-image]');
    this.hoverImage = this.card.querySelector('[data-card-hover-image]');
    this.prevButton = this.querySelector('[data-image-nav="prev"]');
    this.nextButton = this.querySelector('[data-image-nav="next"]');
    this.loader = this.querySelector('[data-image-loader]');
    this.mediaTarget = this.imageArea?.querySelector('[data-product-media]');
    this.productLink = this.imageArea?.querySelector('.product--image');

    if (!this.imageArea || !this.primaryImage || !this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', this.onNavClick);
    this.nextButton.addEventListener('click', this.onNavClick);
    this.prevButton.addEventListener('pointerdown', this.onNavPointerDown);
    this.nextButton.addEventListener('pointerdown', this.onNavPointerDown);
    this.card.addEventListener('product-card-color-selected', this.onColorSelected);
    this.imageArea.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.imageArea.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.imageArea.addEventListener('touchend', this.onTouchEnd, { passive: false });
    this.productLink?.addEventListener('click', this.onProductLinkClick, true);

    if (this.prefersHover()) {
      this.imageArea.addEventListener('pointerenter', this.onImageAreaPointerEnter);
      this.imageArea.addEventListener('pointerleave', this.onImageAreaPointerLeave);
      this.imageArea.addEventListener('mouseover', this.onImageAreaMouseOver);
    }

    this.refreshImages(true);
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onNavClick);
    this.nextButton?.removeEventListener('click', this.onNavClick);
    this.prevButton?.removeEventListener('pointerdown', this.onNavPointerDown);
    this.nextButton?.removeEventListener('pointerdown', this.onNavPointerDown);
    this.card?.removeEventListener('product-card-color-selected', this.onColorSelected);
    this.imageArea?.removeEventListener('touchstart', this.onTouchStart);
    this.imageArea?.removeEventListener('touchmove', this.onTouchMove);
    this.imageArea?.removeEventListener('touchend', this.onTouchEnd);
    this.productLink?.removeEventListener('click', this.onProductLinkClick, true);
    this.imageArea?.removeEventListener('pointerenter', this.onImageAreaPointerEnter);
    this.imageArea?.removeEventListener('pointerleave', this.onImageAreaPointerLeave);
    this.imageArea?.removeEventListener('mouseover', this.onImageAreaMouseOver);
    this.clearHoverSyncFrame();
    this.clearHoverExitTimer();
  }

  clearHoverExitTimer() {
    if (this._hoverExitTimer) {
      window.clearTimeout(this._hoverExitTimer);
      this._hoverExitTimer = null;
    }
  }

  clearHoverSyncFrame() {
    if (this._hoverSyncFrame) {
      cancelAnimationFrame(this._hoverSyncFrame);
      this._hoverSyncFrame = null;
    }
  }

  scheduleHoverSync() {
    this.clearHoverSyncFrame();
    this._hoverSyncFrame = requestAnimationFrame(() => {
      this._hoverSyncFrame = null;
      this.syncHoverState();
    });
  }

  syncHoverState() {
    if (!this.prefersHover() || !this.isEnabled() || !this.imageArea) return;

    const hovered = this.imageArea.matches(':hover');
    if (hovered) {
      this.enterGalleryHover();
      return;
    }

    if (this.isImageAreaHovered) {
      this.exitGalleryHover();
      return;
    }

    this.resetGalleryDisplay();
  }

  onImageAreaPointerEnter() {
    this.clearHoverExitTimer();
    if (this.prefersHover() && this.isEnabled()) {
      this.enterGalleryHover();
    }
  }

  onImageAreaPointerLeave() {
    this.clearHoverExitTimer();
    this._hoverExitTimer = window.setTimeout(() => {
      this._hoverExitTimer = null;
      if (this.imageArea?.matches(':hover')) return;
      this.syncHoverState();
    }, 60);
  }

  onImageAreaMouseOver(event) {
    if (!this.imageArea?.contains(event.target)) return;
    this.clearHoverExitTimer();
    this.scheduleHoverSync();
  }

  prefersHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  isBrowseActive() {
    return this.isImageAreaHovered || this.isTouchBrowsing;
  }

  shouldAbortHoverBrowse() {
    return this.prefersHover() && !this.isImageAreaHovered;
  }

  clearHoverImageTransition() {
    this.hoverImage?.classList.remove('is-image-fading', 'is-gallery-fading');
  }

  resetGalleryDisplay() {
    this.imageArea?.classList.remove('is-image-gallery-hover', 'is-image-gallery-loading');
    this.card?.classList.remove('is-hover-image-visible');
    this.clearHoverImageTransition();
    this.primaryImage?.classList.remove('is-gallery-fading');
    if (this.primaryImage) {
      this.primaryImage.style.removeProperty('opacity');
      this.primaryImage.style.removeProperty('visibility');
    }
  }

  enterGalleryHover() {
    if (!this.isEnabled()) return;

    this.clearHoverImageTransition();
    this.isImageAreaHovered = true;
    const minIndex = this.getMinBrowseIndex();
    if (this.currentIndex < minIndex) {
      this.currentIndex = minIndex;
    }

    const image = this.images[this.currentIndex];
    const reveal = () => {
      if (!this.isImageAreaHovered || !image) return;
      this.applyImage(this.currentIndex, true);
    };

    if (!image) {
      return;
    }

    if (this.isImageCached(image.src)) {
      reveal();
      return;
    }

    this.showLoader();
    this.preloadImage(image).then(() => {
      this.hideLoader();
      reveal();
    });
  }

  exitGalleryHover() {
    if (!this.isImageAreaHovered) {
      this.resetGalleryDisplay();
      return;
    }

    this.isImageAreaHovered = false;
    this.isTouchBrowsing = false;
    this._suppressLinkClick = false;
    this.currentIndex = 0;
    this.isSwitching = false;
    this.hideLoader();
    this.resetGalleryDisplay();

    const swatch = this.getSelectedSwatch();
    if (swatch && this.primaryImage) {
      setProductCardImageSource(
        this.primaryImage,
        swatch.dataset.primarySrc,
        swatch.dataset.primarySrcset
      );
    } else if (this.images[0] && this.primaryImage) {
      setProductCardImageSource(this.primaryImage, this.images[0].src, this.images[0].srcset);
    }

    this.updateNavigation();
  }

  canNavigate() {
    if (!this.isEnabled() || this.isSwitching) return false;
    if (this.prefersHover()) return this.isImageAreaHovered;
    return true;
  }

  getBrowseTarget() {
    if (!this.prefersHover()) {
      return this.primaryImage;
    }

    if (this.isImageAreaHovered) {
      return this.hoverImage || this.primaryImage;
    }

    return null;
  }

  onColorSelected() {
    this.loadedSrc.clear();
    const wasBrowsing = this.isBrowseActive();
    this.isTouchBrowsing = false;
    this.refreshImages(true);
    if (wasBrowsing && this.isEnabled()) {
      this.currentIndex = this.getMinBrowseIndex();
      this.applyImage(this.currentIndex, true);
    }
  }

  onNavClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.currentTarget.disabled) return;

    const direction = event.currentTarget.dataset.imageNav;
    if (direction === 'next') {
      this.showNext();
    } else {
      this.showPrevious();
    }
  }

  onNavPointerDown(event) {
    event.stopPropagation();
    if (event.currentTarget.disabled) {
      event.preventDefault();
    }
  }

  onTouchStart(event) {
    if (!this.isEnabled() || event.touches.length !== 1) return;
    this._suppressLinkClick = false;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event) {
    if (!this.isEnabled() || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
    }
  }

  onTouchEnd(event) {
    if (!this.isEnabled() || event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    event.preventDefault();
    event.stopPropagation();
    this._suppressLinkClick = true;
    this.isTouchBrowsing = true;

    if (deltaX < 0) {
      this.showNext(true, true);
    } else {
      this.showPrevious(true, true);
    }
  }

  onProductLinkClick(event) {
    if (!this._suppressLinkClick) return;
    event.preventDefault();
    event.stopPropagation();
    this._suppressLinkClick = false;
  }

  isEnabled() {
    return this.images.length > 1;
  }

  getSelectedSwatch() {
    return this.card.querySelector('[data-color-swatch][aria-checked="true"]');
  }

  normalizeSrc(src) {
    return normalizeProductCardImageSrc(src);
  }

  buildImagesFromSwatch(swatch) {
    if (!swatch) return [];

    const galleryImages = parseProductCardGalleryImages(swatch.dataset.galleryImages);
    if (galleryImages.length > 0) {
      return galleryImages;
    }

    const images = [];
    const addImage = (src, srcset) => {
      if (!src) return;
      const key = this.normalizeSrc(src);
      if (images.some((image) => this.normalizeSrc(image.src) === key)) return;
      images.push({ src, srcset: srcset || '' });
    };

    addImage(swatch.dataset.primarySrc, swatch.dataset.primarySrcset);
    const hoverImage = getSwatchHoverImage(swatch);
    if (hoverImage) {
      addImage(hoverImage.src, hoverImage.srcset);
    }
    return images;
  }

  buildImagesFromJson() {
    const data = this.imageArea.querySelector('[data-card-gallery-images]');
    if (!data) return [];
    return parseProductCardGalleryImages(data.textContent);
  }

  buildImages() {
    const swatch = this.getSelectedSwatch();
    if (swatch) {
      return this.buildImagesFromSwatch(swatch);
    }

    const jsonImages = this.buildImagesFromJson();
    if (jsonImages.length > 0) {
      return jsonImages;
    }

    const images = [];
    const addFromElement = (image) => {
      if (!image) return;
      const src = image.dataset.original || image.currentSrc || image.src;
      const srcset = image.srcset || '';
      if (!src) return;
      const key = this.normalizeSrc(src);
      if (images.some((item) => this.normalizeSrc(item.src) === key)) return;
      images.push({ src, srcset });
    };

    addFromElement(this.primaryImage);
    addFromElement(this.hoverImage);
    return images;
  }

  refreshImages(resetIndex = false) {
    this.images = this.buildImages();

    if (resetIndex) {
      this.currentIndex = 0;
    } else if (this.currentIndex >= this.images.length) {
      this.currentIndex = Math.max(0, this.images.length - 1);
    }

    const enabled = this.isEnabled();
    this.imageArea.classList.toggle('is-image-gallery-ready', enabled);
    this.toggleAttribute('hidden', !enabled);
    this.setAttribute('aria-hidden', enabled ? 'false' : 'true');

    if (!enabled) {
      this.exitGalleryHover();
      this.hideLoader();
      return;
    }

    if (!this.isBrowseActive()) {
      this.currentIndex = 0;
    }

    this.updateNavigation();
  }

  getMinBrowseIndex() {
    return this.images.length > 1 ? 1 : 0;
  }

  showNext(loop = false, immediate = false) {
    if (!this.canNavigate()) return;

    const minIndex = this.getMinBrowseIndex();
    let nextIndex = this.currentIndex + 1;
    if (nextIndex >= this.images.length) {
      if (!loop) return;
      nextIndex = minIndex;
    }

    this.applyImage(nextIndex, immediate);
  }

  showPrevious(loop = false, immediate = false) {
    if (!this.canNavigate()) return;

    const minIndex = this.getMinBrowseIndex();
    let nextIndex = this.currentIndex - 1;
    if (nextIndex < minIndex) {
      if (!loop) return;
      nextIndex = this.images.length - 1;
    }

    this.applyImage(nextIndex, immediate);
  }

  updateNavigation() {
    if (!this.prevButton || !this.nextButton) return;

    const hasMultiple = this.images.length > 1;
    const minIndex = this.getMinBrowseIndex();
    this.prevButton.disabled = !hasMultiple || this.currentIndex <= minIndex;
    this.nextButton.disabled = !hasMultiple || this.currentIndex === this.images.length - 1;
  }

  showLoader() {
    if (!this.loader) return;
    this.loader.hidden = false;
    this.loader.setAttribute('aria-hidden', 'false');
    this.imageArea?.classList.add('is-image-gallery-loading');
  }

  hideLoader() {
    if (!this.loader) return;
    this.loader.hidden = true;
    this.loader.setAttribute('aria-hidden', 'true');
    this.imageArea?.classList.remove('is-image-gallery-loading');
  }

  isImageCached(src) {
    const key = this.normalizeSrc(src);
    return this.loadedSrc.has(key);
  }

  preloadImage(image) {
    const key = this.normalizeSrc(image.src);
    if (this.isImageCached(image.src)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const preloader = new Image();
      preloader.onload = () => {
        this.loadedSrc.add(key);
        resolve();
      };
      preloader.onerror = () => resolve();
      if (image.srcset) {
        preloader.srcset = image.srcset;
      }
      preloader.src = image.src;
    });
  }

  applyImage(index, immediate = false) {
    const minIndex = this.isBrowseActive() ? this.getMinBrowseIndex() : 0;
    if (index < minIndex) {
      index = minIndex;
    }

    const image = this.images[index];
    if (!image) return;

    this.currentIndex = index;
    const target = this.getBrowseTarget();
    if (!target) return;

    const useHoverLayer = target === this.hoverImage && this.hoverImage;

    const update = () => {
      if (useHoverLayer) {
        this.clearHoverImageTransition();
        setProductCardImageSource(target, image.src, image.srcset);
        this.loadedSrc.add(this.normalizeSrc(image.src));
        this.card.classList.add('is-hover-image-visible');
        if (this.isImageAreaHovered) {
          this.imageArea?.classList.add('is-image-gallery-hover');
        }
      } else {
        setProductCardImageSource(target, image.src, image.srcset);
        this.loadedSrc.add(this.normalizeSrc(image.src));
        if (this.prefersHover() && this.isImageAreaHovered) {
          this.card.classList.remove('is-hover-image-visible');
          this.imageArea?.classList.remove('is-image-gallery-hover');
        }
      }
      this.updateNavigation();
    };

    const runHoverLayerFade = () => {
      if (this.shouldAbortHoverBrowse()) {
        this.isSwitching = false;
        return;
      }

      this.hoverImage?.classList.add('is-gallery-fading');
      window.setTimeout(() => {
        if (this.shouldAbortHoverBrowse()) {
          this.clearHoverImageTransition();
          this.isSwitching = false;
          return;
        }
        update();
        requestAnimationFrame(() => {
          this.clearHoverImageTransition();
          this.isSwitching = false;
        });
      }, this.fadeDuration);
    };

    const runTransition = () => {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (immediate || prefersReducedMotion) {
        update();
        this.isSwitching = false;
        return;
      }

      if (useHoverLayer) {
        this.isSwitching = true;
        runHoverLayerFade();
        return;
      }

      this.isSwitching = true;
      target.classList.add('is-gallery-fading');

      window.setTimeout(() => {
        update();
        requestAnimationFrame(() => {
          target.classList.remove('is-gallery-fading');
          this.isSwitching = false;
        });
      }, this.fadeDuration);
    };

    if (immediate || this.isImageCached(image.src)) {
      this.hideLoader();
      runTransition();
      return;
    }

    this.isSwitching = true;
    this.showLoader();

    this.preloadImage(image).then(() => {
      this.hideLoader();
      runTransition();
    });
  }
}


class ProductCardColorPicker extends HTMLElement {
  constructor() {
    super();
    this.onSwatchClick = this.onSwatchClick.bind(this);
    this.onSwatchPointerEnter = this.onSwatchPointerEnter.bind(this);
    this.onSwatchPointerLeave = this.onSwatchPointerLeave.bind(this);
    this.onSwatchFocus = this.onSwatchFocus.bind(this);
    this.onSwatchBlur = this.onSwatchBlur.bind(this);
    this.onSwatchKeyDown = this.onSwatchKeyDown.bind(this);
    this.onImagePointerEnter = this.onImagePointerEnter.bind(this);
    this.onImagePointerLeave = this.onImagePointerLeave.bind(this);
    this.onListPointerLeave = this.onListPointerLeave.bind(this);
    this.onListScroll = this.onListScroll.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.previewResetTimer = null;
  }

  connectedCallback() {
    this.card = this.closest('[data-product-card]');
    if (!this.card) return;

    this.list = this.querySelector('[data-color-list]');
    this.viewport = this.querySelector('[data-color-viewport]');
    this.swatches = Array.from(this.querySelectorAll('[data-color-swatch]'));
    this.prevButton = this.querySelector('[data-color-nav="prev"]');
    this.nextButton = this.querySelector('[data-color-nav="next"]');
    this.primaryImage = this.card.querySelector('[data-card-primary-image]');
    this.hoverImage = this.card.querySelector('[data-card-hover-image]');
    this.productLink = this.card.querySelector('.product--image');
    this.imageArea = this.card.querySelector('.product--card-image') || this.card.querySelector('[data-product-media]')?.closest('.product--card-image');
    this.selectedSwatch = this.swatches.find((swatch) => swatch.getAttribute('aria-checked') === 'true') || this.swatches[0];
    this.previewSwatch = null;
    this.isImageHovered = false;

    if (!this.list || this.swatches.length === 0) return;

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.onSwatchClick);
      swatch.addEventListener('pointerenter', this.onSwatchPointerEnter);
      swatch.addEventListener('pointerleave', this.onSwatchPointerLeave);
      swatch.addEventListener('focus', this.onSwatchFocus);
      swatch.addEventListener('blur', this.onSwatchBlur);
      swatch.addEventListener('keydown', this.onSwatchKeyDown);
    });

    this.list.addEventListener('scroll', this.onListScroll, { passive: true });
    this.list.addEventListener('pointerleave', this.onListPointerLeave);
    this.prevButton?.addEventListener('click', this.onNavClick);
    this.nextButton?.addEventListener('click', this.onNavClick);
    this.imageArea?.addEventListener('pointerenter', this.onImagePointerEnter);
    this.imageArea?.addEventListener('pointerleave', this.onImagePointerLeave);

    this.resizeObserver = new ResizeObserver(this.onResize);
    if (this.viewport) {
      this.resizeObserver.observe(this.viewport);
    }
    this.resizeObserver.observe(this.list);

    this.updateNavigation();
    requestAnimationFrame(() => this.updateNavigation());
    this.applySelectedImages(this.selectedSwatch, true);
    this.updateProductLink(this.selectedSwatch);
    this.card.classList.remove('is-hover-image-visible');
    this.card?.dispatchEvent(
      new CustomEvent('product-card-color-selected', { detail: { swatch: this.selectedSwatch }, bubbles: false })
    );
  }

  disconnectedCallback() {
    this.swatches?.forEach((swatch) => {
      swatch.removeEventListener('click', this.onSwatchClick);
      swatch.removeEventListener('pointerenter', this.onSwatchPointerEnter);
      swatch.removeEventListener('pointerleave', this.onSwatchPointerLeave);
      swatch.removeEventListener('focus', this.onSwatchFocus);
      swatch.removeEventListener('blur', this.onSwatchBlur);
      swatch.removeEventListener('keydown', this.onSwatchKeyDown);
    });

    this.list?.removeEventListener('scroll', this.onListScroll);
    this.list?.removeEventListener('pointerleave', this.onListPointerLeave);
    this.prevButton?.removeEventListener('click', this.onNavClick);
    this.nextButton?.removeEventListener('click', this.onNavClick);
    this.imageArea?.removeEventListener('pointerenter', this.onImagePointerEnter);
    this.imageArea?.removeEventListener('pointerleave', this.onImagePointerLeave);
    this.clearPreviewResetTimer();
    this.resizeObserver?.disconnect();
  }

  onResize() {
    this.updateNavigation();
  }

  onListScroll() {
    this.updateNavigation();
  }

  onNavClick(event) {
    const direction = event.currentTarget.dataset.colorNav;
    const scrollAmount = Math.max(this.list.clientWidth * 0.75, 120);
    this.list.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  updateNavigation() {
    if (!this.list || !this.prevButton || !this.nextButton) return;

    const hasOverflow = this.list.scrollWidth > this.list.clientWidth + 2;
    this.classList.toggle('has-overflow', hasOverflow);

    if (!hasOverflow) {
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }

    const maxScrollLeft = this.list.scrollWidth - this.list.clientWidth;
    this.prevButton.disabled = this.list.scrollLeft <= 1;
    this.nextButton.disabled = this.list.scrollLeft >= maxScrollLeft - 1;
  }

  isGalleryMode() {
    return this.imageArea?.classList.contains('is-image-gallery-ready');
  }

  shouldSkipPreviewReset(next) {
    if (next && this.list?.contains(next)) return true;
    return this.isGalleryMode() && Boolean(next && this.imageArea?.contains(next));
  }

  onSwatchClick(event) {
    const swatch = event.currentTarget;
    this.clearPreviewResetTimer();
    this.setSelectedSwatch(swatch);
    this.previewSwatch = null;
    if (!this.isGalleryMode()) {
      this.hideOnBodyImage();
    }
  }

  onSwatchPointerEnter(event) {
    this.clearPreviewResetTimer();
    this.previewSwatch = event.currentTarget;
    // Preview this color's normal (primary) image only — not on-body
    this.previewPrimaryImage(this.previewSwatch);
    if (this.isGalleryMode()) return;
    // Keep on-body layer in sync for when pointer moves onto the image
    this.prepareOnBodyImage(this.previewSwatch);
    if (this.isImageHovered) {
      this.showOnBodyImage(this.previewSwatch);
    } else {
      this.hideOnBodyImage();
    }
  }

  onSwatchPointerLeave(event) {
    if (this.shouldSkipPreviewReset(event.relatedTarget)) return;
    this.schedulePreviewReset();
  }

  onListPointerLeave(event) {
    if (this.shouldSkipPreviewReset(event.relatedTarget)) return;
    this.schedulePreviewReset(0);
  }

  onSwatchFocus(event) {
    this.clearPreviewResetTimer();
    this.onSwatchPointerEnter(event);
  }

  onSwatchBlur(event) {
    if (this.shouldSkipPreviewReset(event.relatedTarget)) return;
    this.schedulePreviewReset();
  }

  clearPreviewResetTimer() {
    if (this.previewResetTimer) {
      window.clearTimeout(this.previewResetTimer);
      this.previewResetTimer = null;
    }
  }

  schedulePreviewReset(delay = 60) {
    this.clearPreviewResetTimer();
    this.previewResetTimer = window.setTimeout(() => {
      this.previewResetTimer = null;
      this.previewSwatch = null;
      this.applySelectedImages(this.selectedSwatch, true);
      if (this.isGalleryMode()) return;
      if (this.isImageHovered) {
        this.showOnBodyImage(this.selectedSwatch);
      } else {
        this.hideOnBodyImage();
      }
    }, delay);
  }

  onImagePointerEnter() {
    if (this.isGalleryMode()) return;
    this.isImageHovered = true;
    const activeSwatch = this.previewSwatch || this.selectedSwatch;
    this.showOnBodyImage(activeSwatch);
  }

  onImagePointerLeave() {
    if (this.isGalleryMode()) return;
    this.isImageHovered = false;
    this.hideOnBodyImage();
  }

  onSwatchKeyDown(event) {
    const currentIndex = this.swatches.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.swatches.length;
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + this.swatches.length) % this.swatches.length;
      event.preventDefault();
    } else if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.setSelectedSwatch(event.currentTarget);
      this.previewSwatch = null;
      if (!this.isGalleryMode()) {
        this.hideOnBodyImage();
      }
      return;
    } else {
      return;
    }

    this.swatches[nextIndex].focus();
  }

  setSelectedSwatch(swatch, options = {}) {
    const { scroll = true } = options;
    this.selectedSwatch = swatch;
    this.swatches.forEach((item) => {
      item.setAttribute('aria-checked', item === swatch ? 'true' : 'false');
    });
    this.applySelectedImages(swatch, true);
    this.updateProductLink(swatch);
    this.card?.dispatchEvent(
      new CustomEvent('product-card-color-selected', { detail: { swatch }, bubbles: false })
    );
    if (!scroll || !this.list || typeof swatch.scrollIntoView !== 'function') return;

    // Keep scroll inside the color list only — never move the page.
    const listRect = this.list.getBoundingClientRect();
    const swatchRect = swatch.getBoundingClientRect();
    if (swatchRect.left < listRect.left) {
      this.list.scrollLeft -= listRect.left - swatchRect.left + 8;
    } else if (swatchRect.right > listRect.right) {
      this.list.scrollLeft += swatchRect.right - listRect.right + 8;
    }
  }

  updateProductLink(swatch) {
    if (!this.productLink || !swatch.dataset.variantId) return;
    const baseUrl = this.productLink.getAttribute('href')?.split('?')[0];
    if (!baseUrl) return;
    this.productLink.setAttribute('href', `${baseUrl}?variant=${swatch.dataset.variantId}`);
  }

  applySelectedImages(swatch, updatePrimary = false) {
    if (!swatch) return;

    if (updatePrimary && this.primaryImage) {
      setProductCardImageSource(
        this.primaryImage,
        swatch.dataset.primarySrc,
        swatch.dataset.primarySrcset
      );
    }

    if (!this.isGalleryMode()) {
      this.prepareOnBodyImage(swatch);
    }
  }

  previewPrimaryImage(swatch) {
    if (!this.primaryImage || !swatch?.dataset.primarySrc) return;
    setProductCardImageSource(
      this.primaryImage,
      swatch.dataset.primarySrc,
      swatch.dataset.primarySrcset
    );
  }

  prepareOnBodyImage(swatch) {
    const hoverImage = getSwatchHoverImage(swatch);
    if (!this.hoverImage || !hoverImage?.src) return;
    setProductCardImageSource(this.hoverImage, hoverImage.src, hoverImage.srcset);
  }

  showOnBodyImage(swatch) {
    const hoverImage = getSwatchHoverImage(swatch);
    if (!this.hoverImage || !hoverImage?.src) {
      this.hideOnBodyImage();
      return;
    }

    const src = hoverImage.src;
    const srcset = hoverImage.srcset;
    const currentSrc = this.hoverImage.currentSrc || this.hoverImage.src;
    const alreadyVisible = this.card.classList.contains('is-hover-image-visible');
    const sameImage = currentSrc && currentSrc.split('?')[0] === src.split('?')[0];

    if (sameImage) {
      this.card.classList.add('is-hover-image-visible');
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setProductCardImageSource(this.hoverImage, src, srcset);
      this.card.classList.add('is-hover-image-visible');
      return;
    }

    const preloader = new Image();
    preloader.onload = () => {
      if (!this.isImageHovered) return;
      if (alreadyVisible) {
        this.swapHoverImageWithFade(src, srcset);
      } else {
        setProductCardImageSource(this.hoverImage, src, srcset);
        this.card.classList.add('is-hover-image-visible');
      }
    };
    preloader.onerror = () => {
      if (!this.isImageHovered) return;
      setProductCardImageSource(this.hoverImage, src, srcset);
      this.card.classList.add('is-hover-image-visible');
    };
    preloader.src = src;
  }

  hideOnBodyImage() {
    this.card?.classList.remove('is-hover-image-visible');
    this.hoverImage?.classList.remove('is-image-fading');
  }

  swapHoverImageWithFade(src, srcset) {
    if (!this.hoverImage) return;

    const fadeDuration = PRODUCT_CARD_IMAGE_FADE_MS;
    this.hoverImage.classList.add('is-image-fading');

    window.setTimeout(() => {
      setProductCardImageSource(this.hoverImage, src, srcset);
      requestAnimationFrame(() => {
        this.hoverImage.classList.remove('is-image-fading');
      });
    }, fadeDuration);
  }
}


class ProductCardSizePicker extends HTMLElement {
  constructor() {
    super();
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
    this.onCartPointerUp = this.onCartPointerUp.bind(this);
    this.onSizeButtonClick = this.onSizeButtonClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onBackdropPointerDown = this.onBackdropPointerDown.bind(this);
    this.onBackdropPointerUp = this.onBackdropPointerUp.bind(this);
    this.onBackdropPointerCancel = this.onBackdropPointerCancel.bind(this);
    this.onPanelTransitionEnd = this.onPanelTransitionEnd.bind(this);
    this.triggerButton = null;
    this.homeParent = null;
    this.homeNext = null;
    this.isPortaled = false;
    this._listenersBound = false;
    this._dismissTimer = null;
    this._isClosing = false;
    this._closeTimer = null;
    this._openedAt = 0;
    this._backdropPointerId = null;
    this._outsideClickDelayMs = 500;
    this._mobileTransitionMs = 420;
  }

  connectedCallback() {
    const card = this.closest('[data-product-card]');
    if (card) this.card = card;
    if (!this.card || this._listenersBound) return;

    this._listenersBound = true;
    this.panel = this.querySelector('.product-card-size-picker__panel');
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size-value]'));
    this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition || 0);
    this.colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);
    this.variants = this.parseVariants();
    this.backdrop = this.querySelector('.product-card-size-picker__backdrop');

    this.querySelectorAll('[data-size-picker-close]').forEach((button) => {
      if (button === this.backdrop) {
        button.addEventListener('pointerdown', this.onBackdropPointerDown);
        button.addEventListener('pointerup', this.onBackdropPointerUp);
        button.addEventListener('pointercancel', this.onBackdropPointerCancel);
        return;
      }

      button.addEventListener('click', this.onCloseClick);
    });

    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', this.onSizeButtonClick);
    });

    this.bindCartButtons();

    this.card.querySelectorAll('[data-color-swatch]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        if (this.classList.contains('is-open')) {
          this.updateSizeAvailability();
        }
      });
    });
  }

  disconnectedCallback() {
    if (this._isPortaling || this._isRestoring) return;

    this.unbindDismissListeners();
    this.cartButtons?.forEach((button) => {
      button.removeEventListener('pointerup', this.onCartPointerUp);
    });
    this.sizeButtons?.forEach((button) => {
      button.removeEventListener('click', this.onSizeButtonClick);
    });
    this.querySelectorAll('[data-size-picker-close]').forEach((button) => {
      if (button === this.backdrop) {
        button.removeEventListener('pointerdown', this.onBackdropPointerDown);
        button.removeEventListener('pointerup', this.onBackdropPointerUp);
        button.removeEventListener('pointercancel', this.onBackdropPointerCancel);
        return;
      }

      button.removeEventListener('click', this.onCloseClick);
    });
    this.panel?.removeEventListener('transitionend', this.onPanelTransitionEnd);
    this._listenersBound = false;
    document.documentElement.classList.remove('product-card-size-picker-open');
  }

  bindCartButtons() {
    if (!this.card) return;

    this.cartButtons = Array.from(this.card.querySelectorAll('[data-add-to-cart]'));
    this.cartButtons.forEach((button) => {
      button.setAttribute('type', 'button');
      button.removeEventListener('pointerup', this.onCartPointerUp);
      button.addEventListener('pointerup', this.onCartPointerUp);
    });
  }

  onCloseClick(event) {
    if (this.isOpeningLocked()) return;

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  onBackdropPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this._backdropPointerId = event.pointerId;
  }

  onBackdropPointerCancel() {
    this._backdropPointerId = null;
  }

  onBackdropPointerUp(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this._backdropPointerId !== event.pointerId) return;

    this._backdropPointerId = null;
    if (this.isOpeningLocked()) return;

    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  isOpeningLocked() {
    return this.isMobile() && Date.now() - this._openedAt < this._outsideClickDelayMs;
  }

  unbindDismissListeners() {
    if (this._dismissTimer) {
      window.clearTimeout(this._dismissTimer);
      this._dismissTimer = null;
    }

    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  bindDismissListeners() {
    this.unbindDismissListeners();

    const delay = this.isMobile() ? this._outsideClickDelayMs : 0;
    this._dismissTimer = window.setTimeout(() => {
      this._dismissTimer = null;
      if (!this.classList.contains('is-open')) return;
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      document.addEventListener('keydown', this.onDocumentKeyDown);
    }, delay);
  }

  onDocumentPointerDown(event) {
    if (!this.classList.contains('is-open') || this.isOpeningLocked()) return;
    if (this.contains(event.target) || this.triggerButton?.contains(event.target)) return;
    this.close();
  }

  onPanelTransitionEnd(event) {
    if (event.target !== this.panel || event.propertyName !== 'transform') return;
    this.finishClose(this.triggerButton);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  cancelCloseAnimation() {
    if (this._closeTimer) {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }

    this.panel?.removeEventListener('transitionend', this.onPanelTransitionEnd);
    this._isClosing = false;
    this.classList.remove('is-closing');
  }

  finishClose(triggerButton = this.triggerButton) {
    if (
      !this.classList.contains('is-open') &&
      !this.classList.contains('is-closing') &&
      !this.classList.contains('is-preparing')
    ) {
      return;
    }

    this.cancelCloseAnimation();
    this.classList.remove('is-open', 'is-preparing', 'is-closing');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('product-card-size-picker-open');
    this.sizeButtons.forEach((button) => button.classList.remove('is-loading'));
    this.resetPanelPosition();
    this.restoreFromBody();
    triggerButton?.focus({ preventScroll: true });
    this.triggerButton = null;
  }

  parseVariants() {
    const data = this.querySelector('[data-product-variants]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  mountToBody() {
    if (this.isPortaled) return;
    this.homeParent = this.parentNode;
    this.homeNext = this.nextSibling;
    this._isPortaling = true;
    document.body.appendChild(this);
    this._isPortaling = false;
    this.isPortaled = true;
  }

  restoreFromBody() {
    if (!this.isPortaled || !this.homeParent) return;
    this._isRestoring = true;
    this.homeParent.insertBefore(this, this.homeNext);
    this._isRestoring = false;
    this.isPortaled = false;
  }

  onCartPointerUp(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.currentTarget !== event.target && !event.currentTarget.contains(event.target)) return;

    event.preventDefault();
    event.stopPropagation();
    this._backdropPointerId = null;
    this.open(event.currentTarget);
  }

  onSizeButtonClick(event) {
    const button = event.currentTarget;
    if (button.disabled || button.classList.contains('is-loading')) return;

    const variant = this.findVariant(button.dataset.sizeValue);
    if (!variant) return;

    button.classList.add('is-loading');
    this.addToCart(variant.id, button);
  }

  open(triggerButton) {
    this.cancelCloseAnimation();
    this._openedAt = Date.now();
    this._backdropPointerId = null;

    document.querySelectorAll('product-card-size-picker.is-open, product-card-size-picker.is-closing').forEach((picker) => {
      if (picker !== this) picker.close();
    });

    this.triggerButton = triggerButton;
    this.updateSizeAvailability();
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');

    if (this.isMobile()) {
      this.mountToBody();
      document.documentElement.classList.add('product-card-size-picker-open');
      this.resetPanelPosition();
      this.classList.remove('is-open');
      this.classList.add('is-preparing');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.classList.remove('is-preparing');
          this.classList.add('is-open');
        });
      });
    } else {
      this.restoreFromBody();
      this.classList.add('is-open');
      window.requestAnimationFrame(() => {
        if (triggerButton && this.panel) {
          this.positionPanel(triggerButton);
        }
      });
    }

    this.bindDismissListeners();
  }

  close() {
    if (this._isClosing || (!this.classList.contains('is-open') && !this.classList.contains('is-preparing'))) return;

    this.unbindDismissListeners();
    const triggerButton = this.triggerButton;

    if (this.isMobile() && !this.prefersReducedMotion()) {
      this._isClosing = true;
      this.classList.remove('is-open', 'is-preparing');
      this.classList.add('is-closing');
      document.documentElement.classList.remove('product-card-size-picker-open');

      this.panel?.addEventListener('transitionend', this.onPanelTransitionEnd);
      this._closeTimer = window.setTimeout(() => {
        this.finishClose(triggerButton);
      }, this._mobileTransitionMs + 40);
      return;
    }

    this.finishClose(triggerButton);
  }

  onDocumentKeyDown(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  resetPanelPosition() {
    if (!this.panel) return;
    this.panel.style.top = '';
    this.panel.style.left = '';
    this.panel.style.right = '';
    this.panel.style.bottom = '';
    this.panel.style.minWidth = '';
    this.panel.style.maxWidth = '';
    this.panel.style.position = '';
  }

  positionPanel(triggerButton) {
    if (!this.panel || !triggerButton || !this.card) return;

    const cardRect = this.card.getBoundingClientRect();
    const rect = triggerButton.getBoundingClientRect();
    const panelWidth = Math.min(240, Math.max(168, this.panel.offsetWidth || 168));
    const panelHeight = this.panel.offsetHeight || 220;

    let left = rect.left - cardRect.left;
    left = Math.min(Math.max(8, left), Math.max(8, cardRect.width - panelWidth - 8));

    let top = rect.bottom - cardRect.top + 8;
    if (top + panelHeight > cardRect.height - 8) {
      top = Math.max(8, rect.top - cardRect.top - panelHeight - 8);
    }

    this.panel.style.position = 'absolute';
    this.panel.style.left = `${left}px`;
    this.panel.style.top = `${top}px`;
    this.panel.style.right = 'auto';
    this.panel.style.bottom = 'auto';
    this.panel.style.minWidth = `${panelWidth}px`;
    this.panel.style.maxWidth = `${panelWidth}px`;
  }

  getSelectedColorValue() {
    const selectedSwatch = this.card.querySelector('[data-color-swatch][aria-checked="true"]');
    if (selectedSwatch?.dataset.colorValue) {
      return selectedSwatch.dataset.colorValue;
    }

    const formContext = this.card.querySelector('product-form-context');
    const variantId = Number(formContext?.querySelector('[name="id"]')?.value);
    const currentVariant = this.variants.find((variant) => variant.id === variantId);
    if (!currentVariant || !this.colorOptionPosition) return null;
    return currentVariant[`option${this.colorOptionPosition}`];
  }

  findVariant(sizeValue) {
    const colorValue = this.getSelectedColorValue();
    return this.variants.find((variant) => {
      const sizeMatches = variant[`option${this.sizeOptionPosition}`] === sizeValue;
      if (!sizeMatches) return false;
      if (!this.colorOptionPosition || !colorValue) return true;
      return variant[`option${this.colorOptionPosition}`] === colorValue;
    });
  }

  updateSizeAvailability() {
    const colorValue = this.getSelectedColorValue();
    this.sizeButtons.forEach((button) => {
      const sizeValue = button.dataset.sizeValue;
      const variant = this.variants.find((item) => {
        const sizeMatches = item[`option${this.sizeOptionPosition}`] === sizeValue;
        if (!sizeMatches) return false;
        if (!this.colorOptionPosition || !colorValue) return true;
        return item[`option${this.colorOptionPosition}`] === colorValue;
      });
      button.disabled = !variant || !variant.available;
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    });
  }

  addToCart(variantId, sizeButton) {
    const formContext = this.card.querySelector('product-form-context');
    const form = formContext?.querySelector('form');
    const idInput = form?.querySelector('[name="id"]');

    if (!form || !idInput) {
      sizeButton?.classList.remove('is-loading');
      return;
    }

    idInput.disabled = false;
    idInput.value = variantId;

    const onSubmitEnd = () => {
      sizeButton?.classList.remove('is-loading');
      this.close();
      form.removeEventListener('product-card-atc-complete', onSubmitEnd);
    };

    form.addEventListener('product-card-atc-complete', onSubmitEnd, { once: true });
    form.requestSubmit();
  }
}

  customElements.define('product-card-image-gallery', ProductCardImageGallery);
  customElements.define('product-card-color-picker', ProductCardColorPicker);
  customElements.define('product-card-size-picker', ProductCardSizePicker);
}